import { useCallback, useMemo } from "react"

import * as turf from "@turf/turf"

import { CatalogItemDetailsForm } from "@l2r-front/l2r-common"
import { sanitizeData } from "@l2r-front/l2r-geodata"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { useMapDispatchContext } from "@l2r-front/l2r-map"
import { useNetworkReferential, useSubNetworksWithWriteAccess } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { NavigationOrigins, useNavigateWithSearchParams } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { API_MODULE_VERTICAL_SIGNING } from "../../../../common/constants/modules"
import { useVerticalSignCreate } from "../../hooks/mutations/useVerticalSignCreate"
import { useVerticalSignPoleCreate } from "../../hooks/mutations/useVerticalSignPoleCreate"
import { useVerticalSigningProject } from "../../hooks/queries/verticalSigning/useVerticalSigningProject"

export const VerticalSignPoleDuplicateFormPost = (props) => {

    const {
        close,
        verticalSigns,
        startingPosition,
        startingRoad,
        onMutationRunning,
        ...otherProps
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const navigate = useNavigateWithSearchParams()
    const { setMapPin } = useMapDispatchContext()
    const { setSnackbar } = useAlertsDispatchContext()

    const { data: project } = useVerticalSigningProject()
    const subNetworks = useSubNetworksWithWriteAccess(null, API_MODULE_VERTICAL_SIGNING)
    const { data: referential } = useNetworkReferential({}, {}, subNetworks)
    const sanitizedReferential = useMemo(() => {
        return sanitizeData(referential)
    }, [referential])

    const poleCreationMutation = useVerticalSignPoleCreate({}, {
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.verticalSignPoleDuplicateForm.snackbar.error"),
    })

    const signCreationMutation = useVerticalSignCreate({}, {
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.verticalSignPoleDuplicateForm.snackbar.error"),
    })

    const onSubmit = useCallback(async values => {
        const linearLocation = {
            typeid: 0,
            road: values.road.road,
            start: values.road.start,
            end: values.road.start + 1,
        }
        const point = turf.point([values.position.longitude, values.position.latitude])

        onMutationRunning(true)
        try {
            const pole = await poleCreationMutation.mutateAsync({ project: project.uuid, linear_location: linearLocation, geometry: point.geometry })

            // Signs are created one after the other to keep their order on the new pole
            for (const verticalSign of verticalSigns) {
                await signCreationMutation.mutateAsync({
                    vertical_sign_pole: pole.uuid,
                    vertical_sign_type: verticalSign.vertical_sign_type,
                    condition: verticalSign.condition,
                    content: verticalSign.content,
                    tags: verticalSign.tags,
                })
            }

            setSnackbar({
                label: t(I18N_NAMESPACE, "containers.verticalSignPoleDuplicateForm.snackbar.success"),
                severity: "success",
                anchorOrigin: { horizontal: "left", vertical: "bottom" },
                duration: 5000,
            })
            setMapPin()
            close(false)
            navigate(`../${pole.linear_location.road}/${pole.uuid}`, { navigationOrigin: NavigationOrigins.DETAIL_FROM_DETAIL })
        } catch {
            // Errors are already reported by the mutations' snackbars
        } finally {
            onMutationRunning(false)
        }
    }, [close, poleCreationMutation, signCreationMutation, project, verticalSigns, navigate, setMapPin, setSnackbar, t, onMutationRunning])

    return <CatalogItemDetailsForm
        {...otherProps}
        referential={sanitizedReferential}
        subNetworks={subNetworks}
        startingPosition={startingPosition}
        startingRoad={startingRoad}
        onSubmit={onSubmit}
        onCancel={close}
    />
}

VerticalSignPoleDuplicateFormPost.propTypes = {
    close: PropTypes.func.isRequired,
    verticalSigns: PropTypes.arrayOf(PropTypes.object).isRequired,
    startingPosition: PropTypes.shape({
        longitude: PropTypes.number,
        latitude: PropTypes.number,
    }).isRequired,
    startingRoad: PropTypes.string,
}
